/**
 * Copyright 2025 Beijing Volcano Engine Technology Co., Ltd. All Rights Reserved.
 * SPDX-license-identifier: BSD-3-Clause
 */

import { createSlice, Dispatch } from '@reduxjs/toolkit';
import {
  AudioPropertiesInfo,
  LocalAudioStats,
  NetworkQuality,
  RemoteAudioStats,
} from '@volcengine/rtc';
import RtcClient, { HistoricalMessage } from '@/lib/RtcClient';

export interface IUser {
  username?: string;
  userId?: string;
  publishAudio?: boolean;
  publishVideo?: boolean;
  publishScreen?: boolean;
  audioStats?: RemoteAudioStats;
  audioPropertiesInfo?: AudioPropertiesInfo;
}

export type LocalUser = Omit<IUser, 'audioStats'> & {
  loginToken?: string;
  audioStats?: LocalAudioStats;
};

export interface Msg {
  value: string;
  time: string;
  user: string;
  paragraph?: boolean;
  definite?: boolean;
  isInterrupted?: boolean;
}

export interface SceneConfig {
  id: string;
  icon?: string;
  name?: string;
  questions?: string[];
  botName: string;
  isVision: boolean;
  isScreenMode: boolean;
  isInterruptMode: boolean;
  sessionId: string;
}

export interface RTCConfig {
  AppId: string;
  RoomId: string;
  UserId: string;
  Token: string;
}

export interface RoomState {
  time: number;
  roomId?: string;
  localUser: LocalUser;
  remoteUsers: IUser[];
  autoPlayFailUser: string[];
  isJoined: boolean;
  scene: string;
  sceneConfigMap: Record<string, SceneConfig>;
  rtcConfigMap: Record<string, RTCConfig>;
  isAIGCEnable: boolean;
  isAITalking: boolean;
  isAIThinking: boolean;
  isUserTalking: boolean;
  networkQuality: NetworkQuality;
  msgHistory: Msg[];
  currentConversation: {
    [user: string]: {
      msg: string;
      definite: boolean;
    };
  };
  isShowSubtitle: boolean;
  isFullScreen: boolean;
  customSceneName: string;
}

const initialState: RoomState = {
  time: -1,
  scene: '',
  sceneConfigMap: {},
  rtcConfigMap: {},
  remoteUsers: [],
  localUser: {
    publishAudio: false,
    publishVideo: false,
    publishScreen: false,
  },
  autoPlayFailUser: [],
  isJoined: false,
  isAIGCEnable: false,
  isAIThinking: false,
  isAITalking: false,
  isUserTalking: false,
  networkQuality: NetworkQuality.UNKNOWN,

  msgHistory: [],
  currentConversation: {},
  isShowSubtitle: true,
  isFullScreen: false,
  customSceneName: '',
};

export const roomSlice = createSlice({
  name: 'room',
  initialState,
  reducers: {
    localJoinRoom: (
        state,
        {
          payload,
        }: {
          payload: {
            roomId: string;
            user: LocalUser;
          };
        }
    ) => {
      state.roomId = payload.roomId;
      state.localUser = {
        ...state.localUser,
        ...payload.user,
      };
      state.isJoined = true;
    },
    initHistoryMsg: (state, { payload }: { payload: Msg[] }) => {
      // console.log('初始化历史消息到Redux:', payload);
      // 只保留最近20条数据
      state.msgHistory = payload.slice(0, 50);
    },
    localLeaveRoom: (state) => {
      state.roomId = undefined;
      state.time = -1;
      state.localUser = {
        publishAudio: false,
        publishVideo: false,
        publishScreen: false,
      };
      state.remoteUsers = [];
      state.isJoined = false;
    },
    remoteUserJoin: (state, { payload }) => {
      state.remoteUsers.push(payload);
    },
    remoteUserLeave: (state, { payload }) => {
      const findIndex = state.remoteUsers.findIndex((user) => user.userId === payload.userId);
      state.remoteUsers.splice(findIndex, 1);
    },

    updateScene: (state, { payload }) => {
      state.scene = payload;
    },

    updateSceneConfig: (state, { payload }) => {
      state.sceneConfigMap = payload;
    },

    updateRTCConfig: (state, { payload }) => {
      state.rtcConfigMap = payload;
      RtcClient.basicInfo = {
        app_id: payload[state.scene].AppId,
        room_id: payload[state.scene].RoomId,
        user_id: payload[state.scene].UserId,
        token: payload[state.scene].Token,
      };
    },

    updateLocalUser: (state, { payload }: { payload: Partial<LocalUser> }) => {
      state.localUser = {
        ...state.localUser,
        ...(payload || {}),
      };
    },

    updateNetworkQuality: (state, { payload }) => {
      state.networkQuality = payload.networkQuality;
    },

    updateRemoteUser: (state, { payload }: { payload: IUser | IUser[] }) => {
      if (!Array.isArray(payload)) {
        payload = [payload];
      }

      payload.forEach((user) => {
        const findIndex = state.remoteUsers.findIndex((u) => u.userId === user.userId);
        state.remoteUsers[findIndex] = {
          ...state.remoteUsers[findIndex],
          ...user,
        };
      });
    },

    updateRoomTime: (state, { payload }) => {
      state.time = payload.time;
    },

    addAutoPlayFail: (state, { payload }) => {
      const autoPlayFailUser = state.autoPlayFailUser;
      const index = autoPlayFailUser.findIndex((item) => item === payload.userId);
      if (index === -1) {
        state.autoPlayFailUser.push(payload.userId);
      }
    },
    removeAutoPlayFail: (state, { payload }) => {
      const autoPlayFailUser = state.autoPlayFailUser;
      const _autoPlayFailUser = autoPlayFailUser.filter((item) => item !== payload.userId);
      state.autoPlayFailUser = _autoPlayFailUser;
    },
    clearAutoPlayFail: (state) => {
      state.autoPlayFailUser = [];
    },
    updateAIGCState: (state, { payload }) => {
      state.isAIGCEnable = payload.isAIGCEnable;
    },
    updateAITalkState: (state, { payload }) => {
      state.isAIThinking = false;
      state.isUserTalking = false;
      state.isAITalking = payload.isAITalking;
    },
    updateAIThinkState: (state, { payload }) => {
      state.isAIThinking = payload.isAIThinking;
      state.isUserTalking = false;
    },
    clearHistoryMsg: (state) => {
      state.msgHistory = [];
    },
    setHistoryMsg: (state, { payload }) => {
      const { text, user, paragraph, definite } = payload;
      const botName = state.sceneConfigMap[state.scene]?.botName;
      const fromBot = user === botName;
      const lastMsg = state.msgHistory.at(-1);

      // 判断上一条消息是否已完成
      const lastMsgCompleted = lastMsg
          ? (fromBot ? lastMsg.definite : lastMsg.paragraph)
          : true;

      // 判断是否可以合并到上一条消息（definite=true且同用户且不分段）
      const canMerge = lastMsg
          && definite
          && lastMsg.definite
          && lastMsg.user === user
          && !paragraph;

      if (canMerge) {
        // 合并到上一条消息（保留原格式，拼接内容）
        lastMsg.value += text;
        lastMsg.time = new Date().toString();
      } else if (lastMsg && !lastMsgCompleted) {
        // 上一条消息未完成，直接更新内容
        lastMsg.value = text;
        lastMsg.time = new Date().toString();
        lastMsg.paragraph = paragraph;
        lastMsg.definite = definite;
        lastMsg.user = user;
      } else {
        // 新增消息（满足分段条件或无历史消息）
        state.msgHistory.push({
          value: text,
          time: new Date().toString(),
          user,
          definite,
          paragraph,
        });
      }

      // const { text, user, paragraph, definite } = payload;
      // const botName = state.sceneConfigMap[state.scene]?.botName;
      // const fromBot = user === botName;
      // const lastMsg = state.msgHistory.at(-1);

      // if (state.msgHistory.length) {
      //   if (lastMsgCompleted) {
      //     state.msgHistory.push({
      //       value: payload.text,
      //       time: new Date().toString(),
      //       user: payload.user,
      //       definite,
      //       paragraph,
      //     });
      //   } else {
      //     lastMsg.value = payload.text;
      //     lastMsg.time = new Date().toString();
      //     lastMsg.paragraph = paragraph;
      //     lastMsg.definite = definite;
      //     lastMsg.user = payload.user;
      //   }
      // } else {
      //   state.msgHistory.push({
      //     value: payload.text,
      //     time: new Date().toString(),
      //     user: payload.user,
      //     paragraph,
      //   });
      // }
    },
    setInterruptMsg: (state) => {
      state.isAITalking = false;
      if (!state.msgHistory.length) {
        return;
      }
      for (let id = state.msgHistory.length - 1; id >= 0; id--) {
        const msg = state.msgHistory[id];
        if (msg.value) {
          if (!msg.definite) {
            state.msgHistory[id].isInterrupted = true;
          }
          break;
        }
      }
    },
    clearCurrentMsg: (state) => {
      state.currentConversation = {};
      state.msgHistory = [];
      state.isAITalking = false;
      state.isUserTalking = false;
    },
    updateShowSubtitle: (state, { payload }) => {
      state.isShowSubtitle = payload.isShowSubtitle;
    },
    updateFullScreen: (state, { payload }) => {
      state.isFullScreen = payload.isFullScreen;
    },
    updatecustomSceneName: (state, { payload }) => {
      state.customSceneName = payload.customSceneName;
    },
  },
});

// 改进的异步action用于加载历史消息
export const fetchAndInitHistoryMsg = () => {
  return async (dispatch: Dispatch) => {
    try {
      console.log('开始执行fetchAndInitHistoryMsg');

      // 检查RtcClient是否已初始化
      if (!RtcClient) {
        throw new Error('RtcClient未初始化');
      }

      // 从RtcClient获取历史消息
      console.log('尝试从RtcClient获取历史消息');
      let historicalMessages: HistoricalMessage[] = RtcClient.getHistoricalMessages();
      console.log('从RtcClient获取到的历史消息:', historicalMessages);

      // 如果RtcClient中没有数据，尝试直接从sessionStorage获取
      if (!historicalMessages || historicalMessages.length === 0) {
        console.log('RtcClient中没有历史消息，尝试从sessionStorage获取');
        const subtitleList = sessionStorage.getItem('subtitleList');
        if (subtitleList) {
          try {
            historicalMessages = JSON.parse(subtitleList) as HistoricalMessage[];
            // console.log('从sessionStorage获取到的历史消息:', historicalMessages);
          } catch (e) {
            console.error('解析sessionStorage中的历史消息失败:', e);
          }
        } else {
          console.log('sessionStorage中也没有历史消息');
        }
      }

      // 转换为Redux所需的Msg格式
      const formattedMessages: Msg[] = (historicalMessages || []).map(msg => ({
        value: msg.content,
        time: new Date(msg.timestamp).toString(),
        user: msg.userId,
        definite: true,
        paragraph: true
      }));

      // console.log('格式化后的历史消息:', formattedMessages);

      // 分发action更新状态
      dispatch(roomSlice.actions.initHistoryMsg(formattedMessages));
      return formattedMessages;
    } catch (error) {
      console.error('加载历史消息时发生错误:', error);
      throw error; // 抛出错误，让调用者可以捕获
    }
  };
};

export const {
  localJoinRoom,
  localLeaveRoom,
  remoteUserJoin,
  remoteUserLeave,
  updateRemoteUser,
  updateLocalUser,
  updateRoomTime,
  addAutoPlayFail,
  removeAutoPlayFail,
  clearAutoPlayFail,
  updateAIGCState,
  updateAITalkState,
  updateAIThinkState,
  setHistoryMsg,
  clearHistoryMsg,
  clearCurrentMsg,
  setInterruptMsg,
  updateNetworkQuality,
  updateScene,
  updateSceneConfig,
  updateRTCConfig,
  updateShowSubtitle,
  updateFullScreen,
  updatecustomSceneName,
  initHistoryMsg,
} = roomSlice.actions;

export default roomSlice.reducer;
